import PermissionButton from "../../components/ui/PermissionButton";
import { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import FinancialOwnership from '../../components/ui/FinancialOwnership';

const API = "/api/income";

export default function AddIncomeModal({
  income,
  onClose,
  onSaved,
}) {
  const [accounts, setAccounts] = useState([]);

  const [form, setForm] = useState({
    branch_id:'',department_id:'',
    income_no: "",
    income_date: "",
    account_id: "",
    source: "",
    amount: "",
    payment_mode: "Cash",
    reference: "",
    notes: "",
    attachment: "",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (income) {
      setForm({
        branch_id:income.branch_id||'',department_id:income.department_id||'',
        income_no: income.income_no || "",
        income_date: income.income_date || "",
        account_id: income.account_id || "",
        source: income.source || "",
        amount: income.amount || "",
        payment_mode: income.payment_mode || "Cash",
        reference: income.reference || "",
        notes: income.notes || "",
        attachment: income.attachment || "",
      });
    }
  }, [income]);

  async function loadAccounts() {
    try {
      const res = await fetch(`${API}/accounts/list`, {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setAccounts(data.accounts);
      }
    } catch (err) {
      console.log(err);
    }
  }

  function update(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function submit() {
    const url = income
      ? `${API}/${income.id}`
      : API;

    const method = income ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    });

    const data = await res.json();

    if (data.status === "success") {
      onSaved?.();
      onClose();
    } else {
      alert(data.message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">

      <div className="w-full max-w-4xl rounded-2xl bg-[#0b0b0f] border border-white/10 overflow-hidden">

        {/* Header */}

        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10">

          <div>
            <h2 className="text-2xl font-serif">
              {income ? "Edit Income" : "Add Income"}
            </h2>

            <p className="text-sm text-gray-400 mt-1">
              Record Income Entry
            </p>
          </div>

          <PermissionButton permissionModule="income"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </PermissionButton>

        </div>

        {/* Form */}

        <div className="p-8 space-y-8">
          <FinancialOwnership branchId={form.branch_id} departmentId={form.department_id} locked={Boolean(income?.department_id)} onChange={values=>setForm(prev=>({...prev,...values}))}/>

          <div className="grid md:grid-cols-2 gap-6">

            <Input
              label="Income No"
              value={form.income_no}
              onChange={(v) => update("income_no", v)}
            />

            <Input
              type="date"
              label="Income Date"
              value={form.income_date}
              onChange={(v) => update("income_date", v)}
            />

            <div>

              <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
                Income Account
              </label>

              <select
                value={form.account_id}
                onChange={(e) => update("account_id", e.target.value)}
                className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              >
                <option value="">Select Account</option>

                {accounts.map((a) => (
                  <option
                    key={a.id}
                    value={a.id}
                  >
                    {a.name}
                  </option>
                ))}

              </select>

            </div>

            <Input
              label="Income Source"
              value={form.source}
              onChange={(v) => update("source", v)}
            />

            <Input
              type="number"
              label="Amount"
              value={form.amount}
              onChange={(v) => update("amount", v)}
            />

            <div>

              <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
                Payment Mode
              </label>

              <select
                value={form.payment_mode}
                onChange={(e) =>
                  update("payment_mode", e.target.value)
                }
                className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              >
                <option>Cash</option>
                <option>Bank</option>
                <option>UPI</option>
                <option>Cheque</option>
              </select>

            </div>

            <Input
              label="Reference"
              value={form.reference}
              onChange={(v) => update("reference", v)}
            />

            <Input
              label="Attachment"
              value={form.attachment}
              onChange={(v) => update("attachment", v)}
            />

          </div>

          <div>

            <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
              Notes
            </label>

            <textarea
              rows={5}
              value={form.notes}
              onChange={(e) =>
                update("notes", e.target.value)
              }
              className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 resize-none"
            />

          </div>

          <div className="rounded-xl bg-amber-400/10 border border-amber-400/20 p-6">

            <p className="text-xs uppercase tracking-widest text-gray-400">
              Income Amount
            </p>

            <h2 className="text-4xl mt-3 font-serif text-amber-400">
              ₹{Number(form.amount || 0).toLocaleString("en-IN")}
            </h2>

          </div>

        </div>

        {/* Footer */}

        <div className="flex justify-end gap-4 px-8 py-5 border-t border-white/10">

          <PermissionButton permissionModule="income"
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-white/10 hover:bg-white/5"
          >
            Cancel
          </PermissionButton>

          <PermissionButton permissionModule="income"
            onClick={submit}
            className="px-6 py-2 rounded-xl bg-amber-400 text-black font-semibold flex items-center gap-2"
          >
            <Plus size={16} />

            {income ? "Update Income" : "Save Income"}
          </PermissionButton>

        </div>

      </div>

    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
}) {
  return (
    <div>

      <label className="block mb-2 text-xs uppercase tracking-widest text-gray-400">
        {label}
      </label>

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
      />

    </div>
  );
}
